import { missingFieldLabel, intervalMissingLabel } from './reportingStatus';
import { displayTimepoint } from './displayTimepoint';
import { sourcePaperName } from './sourcePaperName';
import { edgeReviewIndex } from './edgeReviewIds';
import { recordsForLink } from './temporalGraph';
import { edgeRecordGroups, sourceTimingConflict } from './edgeRecordGroups';
const stringify = value => value == null ? '' : String(value);
export const recordedAges = value => [...new Set([...stringify(value).matchAll(/(?:at\s+)?(?:age\s+)?(\d+(?:\.\d+)?)\s*(years?|months?)/gi)].map(match => `${match[1]} ${match[2]}`))];
export function significance(record) {
  const p = (record.statistics || []).filter(stat => stat.type === 'p_value' && (!stat.scope || stat.scope === 'effect_estimate'));
  if (p.length !== 1) {
    const label = record.significance === 'significant' ? 'Significant (reported)'
      : record.significance === 'not_significant' || record.assertion === 'non_significant' ? 'NS (reported)'
      : missingFieldLabel(record, 'significance');
    return { label, p: '', stars: '' };
  }
  const raw = stringify(p[0].value).trim();
  const match = raw.match(/^(?:p\s*)?(<=|>=|<|>|≤|≥|=)?\s*(0?\.\d+|\d+(?:\.\d+)?(?:e[-+]?\d+)?)$/i);
  if (!match) return { label: 'Unclear', p: raw, stars: '' };
  const value = Number(match[2]);
  const operator = match[1] || p[0].p_operator || record.p_operator || '=';
  if (value < 0 || value > 1) return { label: 'Unclear', p: raw, stars: '' };
  let threshold = value;
  // Bounds yield only the guaranteed significance level; no precision is invented.
  if (['>', '>=', '≥'].includes(operator)) return { label: value >= 0.05 ? 'NS' : 'Unclear', p: raw, stars: '' };
  if (['<'].includes(operator)) threshold -= Number.EPSILON;
  const stars = threshold < 0.001 ? '***' : threshold < 0.01 ? '**' : threshold < 0.05 ? '*' : '';
  return { label: stars ? 'Significant' : operator === '<' || operator === '<=' || operator === '≤' ? 'Unclear' : 'NS', p: raw, stars };
}
const normalizeStatistic = value => String(value || '').normalize('NFKC').toLowerCase().replace(/[’‘]/g, "'").replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
const effectLabels = {
  correlation: 'r', r: 'r', 'pearson r': 'Pearson r', 'pearson correlation': 'Pearson r',
  'pearson correlation coefficient': 'Pearson r', "pearson's r": 'Pearson r',
  'spearman r': 'Spearman ρ', 'spearman rho': 'Spearman ρ', 'spearman correlation': 'Spearman ρ', rho: 'ρ', 'kendall tau': 'Kendall τ',
  beta: 'β', 'β': 'β', 'standardized beta': 'Standardized β', 'unstandardized beta': 'Unstandardized β',
  b: 'b', coefficient: 'Coefficient', estimate: 'Estimate', unknown: 'Reported estimate',
  'path coefficient': 'Path coefficient', 'indirect effect': 'Indirect effect',
  loading: 'Factor loading', 'factor loading': 'Factor loading',
  'mean difference': 'Mean difference', 'standardized mean difference': 'Standardized mean difference', 'standardized difference': 'Standardized difference',
  or: 'OR', 'odds ratio': 'OR', rr: 'RR', 'risk ratio': 'RR', hr: 'HR', 'hazard ratio': 'HR',
  "cohen's d": "Cohen's d", "cohen's f2": "Cohen's f²", "cohen's f²": "Cohen's f²",
  'mediated proportion': 'Proportion mediated', 'proportion mediated': 'Proportion mediated',
  covariance: 'Covariance', 'reported estimate with interval': 'Reported estimate',
};
const excludedEstimateType = type => /^(?:p|p value|p only(?: test)?|ci lower|ci upper|ci level|n|sample size|sample n)$/.test(normalizeStatistic(type));
const estimateLabel = type => effectLabels[normalizeStatistic(type)] || String(type || 'Reported estimate').replace(/_/g, ' ');
export function effectSummary(record) {
  const statistics = record.statistics || [];
  const estimates = statistics.filter(stat => !excludedEstimateType(stat.type) && stat.value != null && stat.value !== '' && stat.scope !== 'sample_size');
  if (!estimates.length && record.stat_value != null && record.stat_value !== '' && !excludedEstimateType(record.stat_type)) {
    estimates.push({ type: record.stat_type, value: record.stat_value, unit: record.stat_unit });
  }
  return [...new Set(estimates.map(stat => `${estimateLabel(stat.type)}: ${stat.value}${stat.unit ? ` ${stat.unit}` : ''}`))];
}
export function confidenceInterval(record) {
  const statistics = record.statistics || [];
  const lower = statistics.find(stat => normalizeStatistic(stat.type) === 'ci lower')?.value ?? record.ci_lower;
  const upper = statistics.find(stat => normalizeStatistic(stat.type) === 'ci upper')?.value ?? record.ci_upper;
  return lower != null && lower !== '' && upper != null && upper !== '' ? `[${lower}, ${upper}]` : null;
}

export function effectDirection(record) {
  const valid = ['positive', 'negative', 'mixed', 'none', 'not_applicable'];
  return [record.effect_direction, record.effect_sign, record.direction]
    .map(value => String(value || '').toLowerCase().trim())
    .find(value => valid.includes(value)) || numericalEffectDirection(record) || 'not_reported';
}

export function numericalEffectDirection(record) {
  const sign = (type, raw) => {
    const kind = normalizeStatistic(type);
    const value = String(raw ?? '').trim().replace(/−/g, '-');
    if (!/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?$/i.test(value)) return null;
    const number = Number(value);
    if (!Number.isFinite(number) || !kind || /p value|proportion|percentage|threshold|range|f²|f2/.test(kind)) return null;
    const ratios = ['or', 'odds ratio', 'rr', 'risk ratio', 'relative risk', 'hr', 'hazard ratio'];
    if (ratios.includes(kind)) return number > 0 ? number > 1 ? 'positive' : number < 1 ? 'negative' : 'none' : null;
    const correlation = ['r', 'rho', 'ρ', 'tau', 'τ'].includes(kind) || /pearson|spearman|kendall|correlation/.test(kind);
    const signed = correlation || ['β', 'beta', 'b', 'coefficient', 'covariance', 'estimated change', 'direct effect', 'indirect effect'].includes(kind)
      || /regression coefficient|path (?:coefficient|estimate)|mean difference|change in z.score/.test(kind);
    if (!signed || (correlation && Math.abs(number) > 1)) return null;
    return number > 0 ? 'positive' : number < 0 ? 'negative' : 'none';
  };
  const direct = sign(record.stat_type, record.stat_value);
  if (direct) return direct;
  const signs = [...new Set((record.statistics || []).filter(stat => !stat.scope || stat.scope === 'effect_estimate')
    .map(stat => sign(stat.type, stat.value)).filter(Boolean))];
  return signs.length > 1 ? 'mixed' : signs[0] || null;
}

export function analyticalDirection(record) {
  const aliases = { directed: 'directed', 'one-way': 'directed', 'one way': 'directed',
    bidirectional: 'bidirectional', 'two-way': 'bidirectional', undirected: 'undirected' };
  for (const raw of [record.relation_directionality, record.reported_direction, record.direction]) {
    const value = aliases[String(raw || '').toLowerCase().trim()];
    if (value) return value;
  }
  const method = normalizeStatistic(record.analysis_type || record.method);
  if (/regression|path analysis|mediation|isotemporal substitution/.test(method)) return 'directed';
  if (/correlation|association|anova|chi.square|t.test|between.group|group comparison|group compar|pairwise compar/.test(method)) return 'undirected';
  const type = normalizeStatistic(record.stat_type);
  if (['r', 'rho', 'ρ', 'tau', 'τ'].includes(type) || /pearson|spearman|kendall|correlation/.test(type)) return 'undirected';
  return 'unspecified';
}

export function statisticalSummaryRows(edges, provenance) {
  const reviewIds = edgeReviewIndex(provenance);
  return edges.flatMap((link, edgeIndex) => edgeRecordGroups(recordsForLink(link)).flatMap((group, groupIndex) => group.results.map(({ record }, resultIndex) => ({
    key: `${edgeIndex}:${groupIndex}:${resultIndex}`, link, record, edgeIds: reviewIds(record), graphEdgeIds: [...new Set((link.originalLinks || [link]).map(edge => edge.human_readable_id != null ? `link:${edge.human_readable_id}` : edge.id).filter(Boolean))],
    source: record.source, target: record.target, sourceFile: record.source_file, sourcePaper: sourcePaperName(record, provenance),
    sourceAges: record.source_timepoints?.map(point => displayTimepoint(point.label)) || recordedAges(record.source), targetAges: record.target_timepoints?.map(point => displayTimepoint(point.label)) || recordedAges(record.target), contextAges: recordedAges(record.model_context),
    analysis: [record.model_context?.match(/model\s+\d+[a-z]?/i)?.[0], record.method].filter(Boolean).join(' · ') || record.analysis_type || missingFieldLabel(record, 'analysis_type'),
    direction: effectDirection(record), effect: effectSummary(record), confidenceInterval: confidenceInterval(record), confidenceIntervalMissing: intervalMissingLabel(record), effectMissing: missingFieldLabel(record, 'stat_value', 'Estimate unavailable'), significance: significance(record),
    timingConflict: sourceTimingConflict(record), needsReview: record.needs_review,
    directionality: analyticalDirection(record),
    bidirectional: analyticalDirection(record) === 'bidirectional',
  }))));
}
